import { z } from 'zod';

import { apiClient } from './api-client';

const linkSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  saasProductId: z.string().uuid(),
  externalUserId: z.string().nullable(),
  externalTenantId: z.string().nullable(),
  externalEmail: z.string().nullable(),
  externalDocument: z.string().nullable(),
  authId: z.string().nullable(),
  linkedVia: z.string(),
  matchConfidence: z.enum([
    'exact_high',
    'manually_confirmed',
    'awaiting_review',
    'manually_rejected',
    'new_record',
  ]),
  matchScore: z.number().int().nullable(),
  matchBreakdown: z.unknown(),
  linkedAt: z.coerce.date(),
  lastSeenAt: z.coerce.date().nullable(),
  notes: z.string().nullable(),
  client: z.object({
    id: z.string().uuid(),
    name: z.string(),
    email: z.string().nullable(),
    document: z.string().nullable(),
  }),
  saasProduct: z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
  }),
});

const linksListResponseSchema = z.object({
  items: z.array(linkSchema),
});

const confirmResponseSchema = z.object({
  data: z.object({
    id: z.string().uuid(),
    matchConfidence: linkSchema.shape.matchConfidence,
  }),
});

const rejectResponseSchema = z.object({
  data: z.object({
    rejectionId: z.string().uuid(),
    rejected: z.literal(true),
  }),
});

export type ClientSaasLink = z.output<typeof linkSchema>;
export type ClientSaasLinksListResponse = z.output<typeof linksListResponseSchema>;

export interface LinksFilter {
  clientId?: string;
  saasProductId?: string;
  confidence?:
    | 'exact_high'
    | 'manually_confirmed'
    | 'awaiting_review'
    | 'manually_rejected'
    | 'new_record';
}

export async function fetchSaasLinks(
  filter: LinksFilter = {},
): Promise<ClientSaasLinksListResponse> {
  const qs = new URLSearchParams();
  if (filter.clientId) qs.set('client_id', filter.clientId);
  if (filter.saasProductId) qs.set('saas_product_id', filter.saasProductId);
  if (filter.confidence) qs.set('confidence', filter.confidence);
  const path = qs.toString() ? `/api/client-saas-links?${qs.toString()}` : '/api/client-saas-links';
  const res = await apiClient.get<unknown>(path);
  return linksListResponseSchema.parse(res.data);
}

export async function confirmSaasLink(
  id: string,
): Promise<{ id: string; matchConfidence: ClientSaasLink['matchConfidence'] }> {
  const res = await apiClient.post<unknown>(`/api/client-saas-links/${id}/confirm`, {});
  return confirmResponseSchema.parse(res.data).data;
}

export async function rejectSaasLink(
  id: string,
  reason?: string | null,
): Promise<{ rejectionId: string; rejected: true }> {
  const body = reason ? { reason } : {};
  const res = await apiClient.post<unknown>(`/api/client-saas-links/${id}/reject`, body);
  return rejectResponseSchema.parse(res.data).data;
}
